import { Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DatePipe } from '@angular/common';
import { Treatment } from '../../../../core/models/treatment.model';
import { User } from '../../../../core/models/api/user';
import { Client } from '../../../../core/models/clients.model';
import { TreatmentService } from '../../../../core/services/treatment.service';
import { UserService } from '../../../../core/services/user.service';
import { FormControl, FormGroup } from '@angular/forms';
import { clientSelectionRequiredValidator } from '../../../../core/models/client-selection-required.validator';
import { Subject } from 'rxjs';
import { Board, Card } from '../../../../core/models/board.model';
import firebase from 'firebase/compat/app';
import { ClientService } from '../../../../core/services/client.service';
import moment, { Moment } from 'moment';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { FirstInterviewService } from '../../../../core/services/first-interview.service';
import { Event, EventTypeEnum } from '../../../../core/models/api/event';
import Timestamp = firebase.firestore.Timestamp;


export interface DialogData extends Event {
  daySelected: string[];
  calendarType: number;
  periodicity: number;
}

@Component({
  selector: 'app-new-event-dialog-component',
  templateUrl: 'new-event.component.html'
})
export class NewEventDialogComponent implements OnDestroy {
  public isMobile: boolean = true;

  public daySelected: string[];

  public treatments: Treatment[] = [];
  public treatmentsFiltered: Treatment[];

  public user: User;
  public users: User[] = [];

  public clients: Client[] = [];
  public clientSelected: FormControl<Client> = new FormControl<Client>(null, [ clientSelectionRequiredValidator ]);

  public calendarType: number = 1;
  public periodicity: number = 1;

  public firstInterviewBoardSelected: Board;
  public firstInterviewListSelected: number;
  public firstInterviewCardSelected: Card;

  public isEditMode: boolean;

  public eventType = EventTypeEnum;

  public isCancelled: boolean = false;

  public metaForm = new FormGroup({
    isPaid: new FormControl(null, []),
    isRecovered: new FormControl(null, []),
    isRecovery: new FormControl(null, [])
  });

  private amountOfTreatmentInMinutes: number;

  private destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: Event,
    private dialogRef: MatDialogRef<NewEventDialogComponent>,
    private treatment$: TreatmentService,
    private user$: UserService,
    private client$: ClientService,
    private breakpoint$: BreakpointObserver,
    private firestore$: AngularFirestore,
    private firstInterview$: FirstInterviewService,
    private datePipe: DatePipe
  ) {
    this.breakpoint$.observe('(max-width: 1200px)').subscribe(result => {
      this.isMobile = result.matches;
    });

    this.user$.user$.subscribe(user => this.user = user);

    if (data.id) {
      this.isEditMode = true;
    }

    if (!data.type) {
      data.type = EventTypeEnum.session;
    }

    this.isCancelled = !!data.cancellationType;

    if (data.start instanceof Timestamp) {
      data.start = data.start.toDate();
    }

    if (data.end instanceof Timestamp) {
      data.end = data.end.toDate();
    }

    if (data.clientsId?.length > 0) {
      this.clientSelected.setValue(this.client$.clientsObject[data.clientsId[0]]);
    }

    this.daySelected = [ this.datePipe.transform((data.start as Date), 'c') ];

    this.metaForm.patchValue(data);

    this.user$.users$
      .subscribe((users) => {
        this.users = users.filter(f => f.isEnable);

        this.treatment$.treatments$
          .subscribe((treatments) => {
            this.treatments = treatments;
            this.filterTreatments();
          });
      });

    let firstInterviewBoardDate = new Date().getFullYear();
    if (moment().isBefore(moment().month(8), 'month')) {
      firstInterviewBoardDate = new Date().getFullYear() - 1;
    }
    this.firstInterview$.getBoard(firstInterviewBoardDate).subscribe(board => {
      this.firstInterviewBoardSelected = board;
      if (data.boardCard) {
        const [ board, card ] = data.boardCard.split('-');
        this.firstInterviewBoardSelected.lists.forEach((list, index) => {
          if (list.cards.find(f => f.id === card)) {
            this.firstInterviewListSelected = index;
          }
        });
        this.firstInterviewCardSelected = this.firstInterviewBoardSelected.lists[this.firstInterviewListSelected].cards.find(f => f.id === card);
      }
    });
  }

  onNoClick(): void {
    this.dialogRef.close({ eventType: 'exit' });
  }

  createEvent(): void {
    this.dialogRef.close({
      eventType: 'submit',
      data: {
        daySelected: this.daySelected,
        clientsId: this.clientSelected.value ? [ this.clientSelected.value?.id ] : null,
        calendarType: this.calendarType,
        periodicity: this.periodicity,
        boardCard: this.firstInterviewCardSelected ? `${ this.firstInterviewBoardSelected?.id }-${ this.firstInterviewCardSelected?.id }` : null,
        ...this.data
      }
    });
  }

  onUserChange($event): void {
    this.data.usersId = this.users.filter(u => $event.indexOf(u.id) !== -1).map(m => m.id);
    this.filterTreatments();
  }

  startHourChange(): void {
    this.data.end = moment(this.data.start).add(this.amountOfTreatmentInMinutes, 'minutes').toDate();
    this.filterTreatments();
  }

  filterTreatments(): void {
    this.amountOfTreatmentInMinutes = moment(this.data.end).diff(moment(this.data.start), 'minute');

    this.treatmentsFiltered = this.treatments
      .filter(f => this.users.filter(f => this.data.usersId.indexOf(f.id) !== -1)
        .flatMap(ff => ff?.services).indexOf(f.serviceId) !== -1)
      .filter(f => f.duration === this.amountOfTreatmentInMinutes)
      .filter(f => !f.disabled);

    const treatment = {
      ...this.treatments.find(f => f.id === this.data.treatmentId)
    };

    if (!this.treatmentsFiltered.find(f => f.id === this.data.treatmentId)) {
      treatment.name = '(Deshabilitado, no usar) ' + treatment.name;
      this.treatmentsFiltered.push(treatment);
    }

    this.data.treatmentId = this.data.treatmentId || this.treatmentsFiltered[0]?.id || null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectTab(type: EventTypeEnum): void {
    this.data.type = type;
    this.data.title = null;
    this.firstInterviewListSelected = null;
    this.firstInterviewCardSelected = null;
    this.data.treatmentId = null;
    this.clientSelected.reset(null);

    if (type === EventTypeEnum.firstInterview) {
      this.calendarType = 2;
    } else {
      this.calendarType = 1;
    }
  }

  delete(): void {
    this.dialogRef.close({
      eventType: 'delete',
      data: { ...this.data }
    });
  }

  edit(): void {
    this.dialogRef.close({
      eventType: 'edit',
      data: {
        ...this.data,
        clientsId: this.clientSelected.value ? [ this.clientSelected.value?.id ] : null,
        boardCard: this.firstInterviewCardSelected ? `${ this.firstInterviewBoardSelected?.id }-${ this.firstInterviewCardSelected?.id }` : null
      }
    });
  }

  cancelEvent(): void {
    this.dialogRef.close({
      eventType: 'cancel',
      data: {
        ...this.data,
        clientsId: this.clientSelected.value ? [ this.clientSelected.value?.id ] : null
      }
    });
  }

  restoreEvent(): void {
    this.dialogRef.close({
      eventType: 'restore',
      data: {
        id: this.data.id,
        parentId: this.data.parentId
      }
    });
  }

  changeDay($event: Moment): void {
    this.data.oldStart = this.data.start;
    this.data.start = $event
      .hours((this.data.start as Date).getHours())
      .minutes((this.data.start as Date).getMinutes())
      .toDate();
    this.data.end = $event
      .hours((this.data.end as Date).getHours())
      .minutes((this.data.end as Date).getMinutes())
      .toDate();
  }

  recoveryEvent(): void {
    this.dialogRef.close({
      eventType: 'recovery',
      data: { ...this.data }
    });
  }

  setNoRecovery(): void {
    this.dialogRef.close({
      eventType: 'no-recovery',
      data: { ...this.data }
    });
  }

  async saveMeta(): Promise<void> {
    const { isPaid, isRecovered, isRecovery } = this.metaForm.value;

    await this.firestore$.collection('events').doc(this.data.id).update({ isPaid, isRecovered, isRecovery });
    this.onNoClick();
  }

  showNewCardElements(): void {

  }
}
